import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel,
  Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import { InfoOutlined as InfoIcon, Save as SaveIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  EMPTY_OTHER_TAXES, normalizeOtherTaxes, OTHER_TAX_1_TOOLTIP,
  OTHER_TAX_2_TOOLTIP, OTHER_TAX_EXAMPLES,
} from '../../utils/billTaxes';
import { fetchBillOtherTaxes, saveBillOtherTaxes } from '../../services/adminSettingsService';

/** Business-safe subset of Admin Controls: only bill extra-tax settings. */
export default function BillTaxesSettings() {
  const [taxes, setTaxes] = useState(EMPTY_OTHER_TAXES);
  const [initial, setInitial] = useState(EMPTY_OTHER_TAXES);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBillOtherTaxes()
      .then((data) => {
        const normalized = normalizeOtherTaxes(data);
        setTaxes(normalized);
        setInitial(normalized);
      })
      .catch((err) => setError(err?.response?.data?.message || err?.message || 'Unable to load tax settings.'))
      .finally(() => setLoading(false));
  }, []);

  const update = (key, patch) => {
    setTaxes((current) => ({ ...current, [key]: { ...current[key], ...patch } }));
    setError('');
  };

  const save = async () => {
    const values = [taxes.tax1, taxes.tax2].map((tax) => Number(tax.percent) || 0);
    if (values.some((value) => value < 0 || value > 100)) {
      setError('Tax percentage must be between 0 and 100.');
      return;
    }
    setSaving(true);
    try {
      await saveBillOtherTaxes(taxes);
      setInitial(taxes);
      toast.success('Bill taxes saved');
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Unable to save tax settings.');
    } finally {
      setSaving(false);
    }
  };

  const changed = JSON.stringify(taxes) !== JSON.stringify(initial);
  const slot = (key, tooltip, fallback) => {
    const tax = taxes[key];
    return (
      <Box key={key} sx={{ p: 2, border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FormControlLabel control={<Switch checked={!!tax.enabled} onChange={(e) => update(key, { enabled: e.target.checked })} disabled={saving} />} label={tax.label || fallback} />
          <Tooltip title={tooltip}><InfoIcon fontSize="small" color="action" /></Tooltip>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <TextField label="Label" value={tax.label || ''} onChange={(e) => update(key, { label: e.target.value })} disabled={saving} fullWidth sx={{ maxWidth: 360 }} />
          <TextField label="Percent" type="number" value={tax.percent ?? ''} onChange={(e) => update(key, { percent: e.target.value })} disabled={saving} inputProps={{ min: 0, max: 100, step: 0.1 }} sx={{ width: 150 }} />
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Typography variant="h4" fontWeight={800}>Set taxes</Typography>
      <Typography color="text.secondary" sx={{ mt: 0.5, mb: 2.5 }}>Configure extra charges used in your Vyapar bills.</Typography>
      <Card><CardContent>
        <Typography variant="h6" fontWeight={700}>Bill other taxes</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ my: 1.5 }}>{OTHER_TAX_EXAMPLES}</Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress /></Box> : <>
          {slot('tax1', OTHER_TAX_1_TOOLTIP, 'Service charge')}
          {slot('tax2', OTHER_TAX_2_TOOLTIP, 'Municipal tax')}
          <Button variant="contained" startIcon={<SaveIcon />} onClick={save} disabled={saving || !changed}>{saving ? 'Saving…' : 'Save taxes'}</Button>
        </>}
      </CardContent></Card>
    </Box>
  );
}
